import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import {
    PlatformDataService,
    ShellDomainProvider,
    SqlQueryBuilderService,
    StorageDataService,
    UserInfoService,
    UtilityService
} from '@ecap3/core';
import { TranslateService } from '@ngx-translate/core';
import { find } from 'lodash';
import { FileSaverService } from 'ngx-filesaver';
import { BehaviorSubject, Observable, Subject, firstValueFrom, forkJoin, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ModuleDMSConfig } from 'src/app/delta-config/constant/module-dms.constant';
import { DeltaGqlService } from 'src/app/shared/services/delta-gql.service';
import { DriveFile } from '../models/DriveFile';
import { DriveFolder } from '../models/DriveFolder';

@Injectable({
    providedIn: 'root'
})
export class DocumentManagerService {
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });
    documentShareEvent = new EventEmitter<any>();
    breadCrumbChange = new EventEmitter<any>();
    getChildFolders = new EventEmitter<any>();
    redirectFolder = new EventEmitter<any>();
    refreshFolder = new EventEmitter<any>();
    onShowDetail: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    onFileRemove: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    onTableViewChange: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private observer;
    UploadEvent: Observable<any> = new Observable<any>(
        (observer) => (this.observer = observer)
    );
    FolderEvent: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    private workingStateSource = new BehaviorSubject('busy');
    workingState = this.workingStateSource.asObservable();
    loggedIuUser: any;

    sharedObjectArtifactFields = [
        'ItemId',
        'Name',
        'ParentId',
        'ParentName',
        'Description',
        'WorkSpaceId',
        'WorkSpaceName',
        'StorageAreaId',
        'StorageAreaName',
        'ArtifactType',
        'Extension',
        'FileStorageId',
        'OwnerId',
        'OwnerName',
        'Color',
        'IsFavorite',
        'SharedToUserId',
        'SharedToUserName',
        'SharedToWorkSpaceId',
        'SharedToWorkSpaceName',
        'SharedByUserName',
        'SharedByUserId',
        'OriginalArtifactId',
        'SharingTime',
        'FileSizeInByte',
        'CreateDate'
    ];

    objectArtifactFields = [
        'ItemId',
        'Name',
        'ParentId',
        'ParentName',
        'Description',
        'OrganizationId',
        'SharedOrganizationList',
        'Tags',
        'WorkSpaceId',
        'WorkSpaceName',
        'StorageAreaId',
        'StorageAreaName',
        'ArtifactType',
        'Extension',
        'IsFavorite',
        'FileStorageId',
        'OwnerId',
        'OwnerName',
        'CreateDate',
        'Color',
        'SharedRoleList',
        'SharedUserIdList',
        'SharedPersonIdList',
        'FileSizeInByte',
        'PowerUserSharedInfoList',
        'Secured'
    ];

    constructor(
        private http: HttpClient,
        private _StorageDataService: StorageDataService,
        private platformDataService: PlatformDataService,
        private queryBuilderService: SqlQueryBuilderService,
        private userInfoService: UserInfoService,
        private utilityService: UtilityService,
        private snackBar: MatSnackBar,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private _FileSaverService: FileSaverService,
        private deltagqlService: DeltaGqlService,
        private sanitizer: DomSanitizer
    ) {}

    changeworkingState(message: string) {
        this.workingStateSource.next(message);
    }

    pushData(value) {
        if (this.observer) this.observer.next(value);
    }

    GetAllDocuments(ParentId, ModeuleWorkspaceId, key, archive) {
        return new Promise<any>((resolve, reject) => {
            const promises = [];
            promises.push(
                this.GetFiles(
                    ParentId,
                    ModeuleWorkspaceId,
                    key,
                    archive,
                    0,
                    100,
                    null
                )
            );
            promises.push(
                this.GetFolders(
                    ParentId,
                    ModeuleWorkspaceId,
                    key,
                    archive,
                    0,
                    100,
                    null
                )
            );
            promises.push(
                this.GetSharedFolders(ParentId, key, archive, 0, 100, null)
            );
            promises.push(
                this.GetSharedFiles(ParentId, key, archive, 0, 100, null)
            );
            Promise.all(promises).then(
                (CombinedResp) => {
                    resolve(this.GetFormattedFileAndFolderInfo(CombinedResp));
                },
                (error) => {
                    reject(new Error(error));
                }
            );
        });
    }

    GetAllDocumentsNew(parentId, modeuleWorkspaceId, key, archive) {
        const removeDuplicates = (data) => {
            return data.reduce((acc, current) => {
                const x = acc.find(
                    (item) =>
                        item.OriginalArtifactId === current.OriginalArtifactId
                );
                if (!x) {
                    return acc.concat([current]);
                } else {
                    return acc;
                }
            }, []);
        };

        return new Promise<any>((resolve, reject) => {
            this.http
                .post(
                    ShellDomainProvider['api'].BusinessService +
                        'DeltaService/Dms/GetAllDocuments',
                    {
                        ParentId: parentId,
                        IsArchived: archive,
                        WorkSpaceId: modeuleWorkspaceId,
                        SearchKey: key,
                        SearchAllSubFolders: false
                    },
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .subscribe({
                    next: (resp: any) => {
                        if (resp.Data[2].length) {
                            resp.Data[2] = removeDuplicates(resp.Data[2]);
                        }

                        if (resp.Data[3].length) {
                            resp.Data[3] = removeDuplicates(resp.Data[3]);
                        }
                        resolve(this.GetFormattedFileAndFolderInfo(resp.Data));
                    }
                });
        });
    }

    private GetFormattedFileAndFolderInfo(CombinedResp: any[]) {
        console.log(CombinedResp);
        const files: DriveFile[] = [];
        const folders: DriveFolder[] = [];
        CombinedResp[0].forEach((file) => {
            if (file.WorkSpaceId == ModuleDMSConfig.SharedWorkspaceId) {
                files.push(
                    new DriveFile(
                        file.ParentId,
                        file.IsFavorite,
                        file.OwnerName,
                        file.SharedByUserName,
                        '',
                        '',
                        file.ItemId,
                        '',
                        file.Name,
                        file.Description,
                        '',
                        file.CreateDate,
                        file.FileStorageId,
                        file.OrganizationId,
                        file.FileSizeInByte,
                        file.Type,
                        file.Url,
                        '',
                        false,
                        file.IsArchived,
                    )
                );
                file.Type = 'SharedFile';
                files[files.length - 1] = new DriveFile(
                    file.ParentId,
                    file.IsFavorite,
                    file.OwnerName,
                    file.SharedByUserName,
                    file.SharedToUserId,
                    file.OwnerId,
                    file.ItemId,
                    file['OriginalArtifactId'],
                    file.Name,
                    file.Description,
                    '',
                    file.CreateDate,
                    file.FileStorageId,
                    file.OrganizationId,
                    file.FileSizeInByte,
                    file.Type,
                    file.Url
                );
            }
        });
        CombinedResp[1].forEach((folder) => {
            folders.push(
                new DriveFolder(
                    folder.ParentId,
                    folder.IsFavorite,
                    folder.OwnerName,
                    folder.SharedByUserName,
                    '',
                    '',
                    folder.OrganizationId,
                    folder.ItemId,
                    folder.Name,
                    folder.Description,
                    folder.Color,
                    folder.CreateDate,
                    folder.SharedUserIdList,
                    folder.SharedRoleList,
                    ''
                )
            );
            if (folder.WorkSpaceId == ModuleDMSConfig.SharedWorkspaceId) {
                folder.Type = 'SharedFolder';
                folders[folders.length - 1] = new DriveFolder(
                    folder.ParentId,
                    folder.IsFavorite,
                    folder.OwnerName,
                    folder.SharedByUserName,
                    folder.SharedToUserId,
                    folder.OwnerId,
                    folder.OrganizationId,
                    folder.ItemId,
                    folder.Name,
                    folder.Description,
                    folder.Color,
                    folder.CreateDate,
                    folder.SharedUserIdList,
                    folder.SharedRoleList,
                    folder['OriginalArtifactId'],
                    folder.Type
                );
            }
        });
        CombinedResp[2].forEach((folder) => {
            if (
                this.loggedIuUser?.UserId != folder.OwnerId ||
                folder.WorkSpaceId == ModuleDMSConfig.SharedWorkspaceId
            ) {
                folder.Type = 'SharedFolder';
                folders.push(
                    new DriveFolder(
                        folder.ParentId,
                        folder.IsFavorite,
                        folder.OwnerName,
                        folder.SharedByUserName,
                        folder.SharedToUserId,
                        folder.OwnerId,
                        folder.OrganizationId,
                        folder.ItemId,
                        folder.Name,
                        folder.Description,
                        folder.Color,
                        folder.CreateDate,
                        folder.SharedUserIdList,
                        folder.SharedRoleList,
                        folder['OriginalArtifactId'],
                        folder.Type
                    )
                );
            }
        });
        CombinedResp[3].forEach((file) => {
            if (
                this.loggedIuUser?.UserId != file.OwnerId ||
                file.WorkSpaceId == ModuleDMSConfig.SharedWorkspaceId
            ) {
                file.Type = 'SharedFile';
                files.push(
                    new DriveFile(
                        file.ParentId,
                        file.IsFavorite,
                        file.OwnerName,
                        file.SharedByUserName,
                        file.SharedToUserId,
                        file.OwnerId,
                        file.ItemId,
                        file['OriginalArtifactId'],
                        file.Name,
                        file.Description,
                        '',
                        file.CreateDate,
                        file.FileStorageId,
                        file.OrganizationId,
                        file.FileSizeInByte,
                        file.Type,
                        file.Url
                    )
                );
            }
        });
        return {
            Folders: folders,
            Files: files
        };
    }

    GetRandomColor() {
        const letters = '0123456789ABCDEF';
        let color = '#';
        for (let i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    }

    GetTextColorFromBgColor(color) {
        const hex = color.replace('#', '');
        const c_r = parseInt(hex.substr(0, 2), 16);
        const c_g = parseInt(hex.substr(2, 2), 16);
        const c_b = parseInt(hex.substr(4, 2), 16);
        const brightness = (c_r * 299 + c_g * 587 + c_b * 114) / 1000;
        return brightness > 155 ? 'black' : 'white';
    }

    UploadAllFiles(
        Files: File[],
        ParentFolderId: string,
        OrganizationId: string,
        UseLicensing: boolean,
        ModuleWorkspaceId: string,
        zoneNote?: string
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            if (Files.length === 0) {
                return reject({
                    success: false,
                    error: new Error('No Files Selected')
                });
            }

            const fileReadingPromises = Files.map((file) =>
                this.ReadFileFromBrowser(file)
            );

            Promise.all(fileReadingPromises)
                .then((fileReadingPromisesResp) =>
                    this.handleFileReadResponses(
                        fileReadingPromisesResp,
                        ParentFolderId,
                        OrganizationId,
                        UseLicensing,
                        ModuleWorkspaceId,
                        zoneNote
                    )
                )
                .then((resp) => resolve(resp))
                .catch((error) =>
                    reject({ success: false, error: new Error(error) })
                );
        });
    }

    private handleFileReadResponses(
        fileReadingPromisesResp: any[],
        ParentFolderId: string,
        OrganizationId: string,
        UseLicensing: boolean,
        ModuleWorkspaceId: string,
        zoneNote?: string
    ): Promise<any> {
        const fileUploadPromises = fileReadingPromisesResp
            .filter((promise) => promise.success)
            .map((promise) =>
                this.UploadFileToDMS(
                    promise.file,
                    promise.fileObj,
                    ParentFolderId,
                    OrganizationId,
                    UseLicensing,
                    ModuleWorkspaceId,
                    zoneNote
                )
            );

        if (fileUploadPromises.length === 0) {
            return Promise.reject({
                success: false,
                error: new Error('Something went wrong')
            });
        }

        return Promise.all(fileUploadPromises).then((fileUploadPromiseResp) => {
            const result = fileUploadPromiseResp
                .filter((fileUploadPromise) => fileUploadPromise.success)
                .map((fileUploadPromise) => fileUploadPromise.resp);

            return { success: true, result };
        });
    }

    ReadFileFromBrowser(File) {
        return new Promise<any>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve({
                    success: true,
                    file: File,
                    fileObj: e.target['result']
                });
            };
            reader.onerror = (e) => {
                resolve({
                    success: false,
                    error: e
                });
            };
            reader.readAsArrayBuffer(File);
        });
    }
    uploadPresignedFile(url, file): Observable<any> {
        if (ShellDomainProvider['api'].IsAzStorage) {
            return this.http
                .put(url, file, { headers: { 'x-ms-blob-type': 'BlockBlob' } })
                .pipe(
                    map((response: any) => {
                        return {
                            ItemId: file.ItemId
                        };
                    })
                );
        }
        return this.http.put(url, file).pipe(
            map((response: any) => {
                return response;
            })
        );
    }
    uploadPresignedFileWithProgress(FileInfo, url, file): Observable<any> {
        return new Observable((observer) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', url);
            xhr.setRequestHeader('content-type', file.type);
            if (ShellDomainProvider['api'].IsAzStorage) {
                xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
            }
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const uploadPercentage = (event.loaded / event.total) * 100;
                    observer.next({
                        uploadPercentage: uploadPercentage,
                        file: FileInfo
                    });
                }
            });
            xhr.upload.addEventListener('error', (event) => {
                observer.error({ error: event, file: FileInfo });
            });
            xhr.onreadystatechange = function () {
                if (xhr.readyState === 4) {
                    if (xhr['status'] === 200) {
                        observer.next({
                            uploadPercentage: 100,
                            file: FileInfo
                        });
                        observer.complete();
                    } else {
                        observer.error({
                            error: 'upload error',
                            file: FileInfo
                        });
                    }
                }
            };
            xhr.send(file);
        });
    }
    UploadFileToDMS(
        FileInfo,
        FileData,
        ParentFolderId,
        OrganizationId,
        UseLicensing,
        ModuleWorkspaceId,
        zoneNote?
    ) {
        return new Promise<any>((resolve, reject) => {
            this.GetPresignedURL(
                FileInfo,
                ParentFolderId,
                OrganizationId,
                UseLicensing,
                ModuleWorkspaceId
            ).then(
                (PreSignedURL) => {
                    this.uploadPresignedFile(PreSignedURL, FileInfo)
                        .pipe(take(1))
                        .subscribe({
                            next: (event) => {
                                if (!zoneNote) {
                                    this.pushData({
                                        ItemId: FileInfo.ItemId,
                                        Percentage: 100
                                    });
                                }
                                resolve({
                                    success: true,
                                    resp: event
                                });
                            },
                            error: (error) => {
                                if (!zoneNote) {
                                    this.pushData({
                                        ItemId: error['file']['ItemId'],
                                        Percentage: -1
                                    });
                                }
                                resolve({
                                    success: false,
                                    error: new Error(error)
                                });
                            }
                        });
                },
                (error) => {
                    if (error.ErrorMessages) {
                        this.snackBar.open(
                            this.translateService.instant(
                                'DMS_ERROR.' +
                                    error.ErrorMessages[0]
                                        .toUpperCase()
                                        .replace(/ /g, '_')
                            ) + FileInfo.name,
                            null,
                            {
                                duration: 3000,
                                verticalPosition: 'bottom',
                                horizontalPosition: 'end'
                            }
                        );
                    }
                    this.pushData({
                        ItemId: FileInfo['ItemId'],
                        Percentage: -1
                    });
                    resolve({
                        success: false,
                        error: new Error(error)
                    });
                }
            );
        });
    }

    GetPresignedURL(
        FileInfo,
        ParentFolderId,
        OrganizationId,
        UseLicensing,
        ModuleWorkspaceId,
        FileStorageId = this.utilityService.getNewGuid(),
        FeatureId?: string
    ) {
        return new Promise<any>((resolve, reject) => {
            const userInfo = this.userInfoService.getUserInfo();
            if (userInfo && userInfo.IsSuccess) {
                this.GetWorkSpaceId(false).then(
                    (WorkSpaceId) => {
                        if (ModuleWorkspaceId) WorkSpaceId = ModuleWorkspaceId;
                        const payload = {
                            FileStorageId: FileStorageId,
                            Description: null,
                            Tags: ['upload'],
                            ParentId:
                                ParentFolderId === '' ? null : ParentFolderId,
                            FileName: FileInfo['name'],
                            StorageAreaId:
                                ShellDomainProvider['api'].StorageAreaId,
                            ObjectArtifactId: FileInfo['ItemId'],
                            WorkspaceId: WorkSpaceId,
                            UserId: userInfo.UserId,
                            OrganizationId: OrganizationId,
                            UseLicensing: UseLicensing,
                            FileSizeInByte: FileInfo.size,
                            FeatureId: FeatureId
                        };
                        this.http
                            .post(
                                ShellDomainProvider['api']
                                    .DocumentManagerService +
                                    'DmsCommand/UploadFile',
                                payload,
                                {
                                    headers: this.header,
                                    withCredentials: true,
                                    observe: 'body'
                                }
                            )
                            .pipe(take(1))
                            .subscribe({
                                next: (response: any) => {
                                    if (
                                        response.statusCode === 0 ||
                                        response.StatusCode === 0
                                    ) {
                                        if (
                                            response.uploadStorageUri !==
                                                null ||
                                            response.UploadStorageUri !== null
                                        ) {
                                            resolve(
                                                response.uploadStorageUri ||
                                                    response.UploadStorageUri
                                            );
                                        } else {
                                            reject(
                                                new Error(
                                                    'storage uri not found'
                                                )
                                            );
                                        }
                                    } else {
                                        reject(new Error(response));
                                    }
                                },
                                error: (error) => {
                                    reject(new Error(error));
                                }
                            });
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
            }
        });
    }

    GetWorkSpaceId(IsShared: boolean): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            const fields = [
                'ItemId',
                'Name',
                'Description',
                'IsShared',
                'IsDefault',
                'OwnerId',
                'StorageAreaId',
                'TotalStorageSpace',
                'UsedStorageSpace'
            ];
            const filters = [
                {
                    property: 'IsShared',
                    operator: '=',
                    value: IsShared
                }
            ];
            const query = this.queryBuilderService.prepareQuery(
                'Workspace',
                fields,
                filters,
                null,
                0,
                1
            );

            firstValueFrom(this.platformDataService.getBySqlFilter('Workspace', query, null))
                .then(response => {
                    if (response.body.StatusCode === 0 && response.body.Results.length > 0) {
                        return resolve(response.body.Results[0]['ItemId']);
                    }
                    return this.handleNoResults(query, resolve, reject);
                })
                .catch(error => {
                    reject(new Error(error));
                });
        });
    }

    private handleNoResults(query: any, resolve: (value?: any) => void, reject: (reason?: any) => void): Promise<any> {
        return this.CreateWorkspace()
            .then(() => firstValueFrom(this.platformDataService.getBySqlFilter('Workspace', query, null)))
            .then((res) => {
                if (res.body.StatusCode === 0 && res.body.Results.length > 0) {
                   return resolve(res.body.Results[0]['ItemId']);
                } else {
                    reject(new Error(res.body));
                }
            })
            .catch(error => {
                reject(new Error(error));
            });
    }


    CreateWorkspace() {
        return new Promise<any>((resolve, reject) => {
            const userInfo = this.userInfoService.getUserInfo();
            if (userInfo && userInfo.IsSuccess) {
                const payload = {
                    OwnerId: userInfo.UserId,
                    UserId: userInfo.UserId,
                    TenantId: ShellDomainProvider['api'].Client_ID,
                    TotalStorageSpace: 1000
                };
                this.CreateUserWorkspace(payload).then(
                    (resp) => {
                        this.CreateSharedWorkspace(payload).then(
                            (res) => {
                                resolve(res);
                            },
                            (error) => {
                                reject(new Error(error));
                            }
                        );
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
            }
        });
    }

    CreateUserWorkspace(payload) {
        return new Promise<any>((resolve, reject) => {
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/CreateUserWorkspace ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    CreateSharedWorkspace(payload) {
        return new Promise<any>((resolve, reject) => {
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/CreateSharedWorkspace',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    CreateFolder(
        FolderName,
        FolderDescription,
        ParentFolderId,
        OrganizationId,
        Secured,
        ObjectArtifactId,
        ModuleWorkspaceId
    ) {
        return new Promise<any>((resolve, reject) => {
            const userInfo = this.userInfoService.getUserInfo();
            if (userInfo && userInfo.IsSuccess) {
                this.GetWorkSpaceId(false).then(
                    (WorkSpaceId) => {
                        if (ModuleWorkspaceId) WorkSpaceId = ModuleWorkspaceId;
                        if (ParentFolderId === '') {
                            ParentFolderId = null;
                        }

                        const payload = {
                            Name: FolderName,
                            Description: FolderDescription,
                            ParentId: ParentFolderId,
                            StorageAreaId:
                                ShellDomainProvider['api'].StorageAreaId,
                            WorkspaceId: WorkSpaceId,
                            Tags: ['create_folder'],
                            UserId: userInfo.UserId,
                            OrganizationId: OrganizationId,
                            Secured: Secured,
                            ObjectArtifactId: ObjectArtifactId
                        };
                        this.http
                            .post(
                                ShellDomainProvider['api']
                                    .DocumentManagerService +
                                    'DmsCommand/CreateFolder',
                                payload,
                                {
                                    headers: this.header,
                                    withCredentials: true,
                                    observe: 'body'
                                }
                            )
                            .pipe(take(1))
                            .subscribe({
                                next: (response: any) => {
                                    if (
                                        response.statusCode === 0 ||
                                        response.StatusCode === 0
                                    ) {
                                        resolve(response);
                                    } else {
                                        reject(new Error(response));
                                    }
                                },
                                error: (error) => {
                                    reject(new Error(error));
                                }
                            });
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
            }
        });
    }

    GetFolders(
        ParentId,
        ModuleWorkspaceId,
        key,
        archive,
        PageNumber?,
        PageSize?,
        OrderBy?
    ) {
        return new Promise<any>((resolve, reject) => {
            const fields = this.objectArtifactFields;
            const filters = [
                {
                    property: 'ArtifactType',
                    operator: '=',
                    value: '1'
                }
            ];
            if (!archive) {
                if (ParentId) {
                    filters.push({
                        property: 'ParentId',
                        operator: '=',
                        value: ParentId
                    });
                } else {
                    filters.push({
                        property: 'ParentId',
                        operator: '=',
                        value: '$null'
                    });
                }
            }
            if (ModuleWorkspaceId) {
                filters.push({
                    property: 'WorkSpaceId',
                    operator: '=',
                    value: ModuleWorkspaceId
                });
            }
            if (key.length > 0) {
                filters.push({
                    property: 'Name',
                    operator: 'search',
                    value: key
                });
            }
            filters.push({
                property: 'Name',
                operator: '!=',
                value: ModuleDMSConfig.FolderNameAtCreation
            });
            if (archive !== null) {
                filters.push({
                    property: 'IsArchived',
                    operator: '=',
                    value: archive
                });
            }
            const query = this.queryBuilderService.prepareQuery(
                'ObjectArtifact',
                fields,
                filters,
                null,
                PageNumber,
                PageSize
            );
            firstValueFrom(this.platformDataService
                .getBySqlFilter('ObjectArtifact', query, null))
                .then(
                    (Response) => {
                        if (Response.body.StatusCode === 0) {
                            Response.body.Results.map((x) => {
                                x.Type = 'Folder';
                                return x;
                            });
                            resolve(Response.body.Results);
                        } else {
                            reject(new Error(Response.body));
                        }
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    GetFiles(
        ParentId,
        ModuleWorkspaceId,
        key,
        archive,
        PageNumber,
        PageSize,
        OrderBy
    ) {
        return new Promise<any>((resolve, reject) => {
            const objectArtifactFields = this.objectArtifactFields;
            const filters = [
                {
                    property: 'ArtifactType',
                    operator: '=',
                    value: '2'
                }
            ];
            if (!archive) {
                if (ParentId) {
                    filters.push({
                        property: 'ParentId',
                        operator: '=',
                        value: ParentId
                    });
                } else {
                    filters.push({
                        property: 'ParentId',
                        operator: '=',
                        value: '$null'
                    });
                }
            }
            if (ModuleWorkspaceId) {
                filters.push({
                    property: 'WorkSpaceId',
                    operator: '=',
                    value: ModuleWorkspaceId
                });
            }
            if (key.length > 0) {
                filters.push({
                    property: 'Name',
                    operator: 'search',
                    value: key
                });
            }
            filters.push({
                property: 'Name',
                operator: '!=',
                value: ModuleDMSConfig.FolderNameAtCreation
            });
            if (archive !== null) {
                filters.push({
                    property: 'IsArchived',
                    operator: '=',
                    value: archive
                });
            }
            const query = this.queryBuilderService.prepareQuery(
                'ObjectArtifact',
                objectArtifactFields,
                filters,
                null,
                PageNumber,
                PageSize
            );
            firstValueFrom(this.platformDataService
                .getBySqlFilter('ObjectArtifact', query, null))
                .then(
                    (Response) => {
                        if (Response.body.StatusCode === 0) {
                            Response.body.Results.map((x) => {
                                x.Type = 'File';
                                return x;
                            });
                            resolve(Response.body.Results);
                        } else {
                            reject(new Error(Response.body));
                        }
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    GetSharedFolders(
        ParentId: string | null,
        key: string,
        archive: boolean,
        PageNumber: number,
        PageSize: number,
        OrderBy: string
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            const query = this.buildSharedFileOrFolderQuery(ParentId, key, archive, PageNumber, PageSize);

            firstValueFrom(this.platformDataService.getBySqlFilter('SharedObjectArtifact', query, null))
                .then(response => {
                    if (response.body.StatusCode === 0) {
                        const sharedFolders = this.processSharedFileOrFolder(response.body.Results, 'SharedFolder');
                        resolve(sharedFolders);
                    } else {
                        reject(new Error(response.body));
                    }
                })
                .catch(error => {
                    reject(new Error(error));
                });
        });
    }

    private buildSharedFileOrFolderQuery(ParentId: string | null, key: string, archive: boolean, PageNumber: number, PageSize: number): string {
        const fields = this.sharedObjectArtifactFields.join(',');
        const baseQuery = `Select<${fields}> from<SharedObjectArtifact> where<ArtifactType=__eql(1)`;

        let conditions = '';

        if (!archive) {
            conditions += ParentId
                ? ` & ParentId=__eql(${ParentId})`
                : ' & (IsInDefaultList=__eql(true) | ParentId=__eql($null))';
        }

        if (key) {
            conditions += `& Name=__reg(/${key}/i)`;
        }

        conditions += `& Name=__ne(${ModuleDMSConfig.FolderNameAtCreation})`;
        conditions += `& IsArchived=__eql(${archive})`;

        return `${baseQuery}${conditions}> pageNumber=<${PageNumber}> pageSize=<${PageSize}>`;
    }

    private processSharedFileOrFolder(results: any[], type: 'SharedFolder' | 'SharedFile'): any[] {
        return results.map(x => {
            x.Type = type;
            return x;
        }).reduce((acc, current) => {
            const exists = acc.some(item => item.OriginalArtifactId === current.OriginalArtifactId);
            if (!exists) {
                acc.push(current);
            }
            return acc;
        }, []);
    }


    GetSharedFiles(
        ParentId: string | null,
        key: string,
        archive: boolean,
        PageNumber: number,
        PageSize: number,
        OrderBy: string
    ): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            const query = this.buildSharedFileOrFolderQuery(ParentId, key, archive, PageNumber, PageSize);

            firstValueFrom(this.platformDataService.getBySqlFilter('SharedObjectArtifact', query, null))
                .then(response => {
                    if (response.body.StatusCode === 0) {
                        const sharedFiles = this.processSharedFileOrFolder(response.body.Results, 'SharedFile');
                        resolve(sharedFiles);
                    } else {
                        reject(new Error(response.body));
                    }
                })
                .catch(error => {
                    reject(new Error(error));
                });
        });
    }

    GetCurrentUploadQueue() {
        return new Promise<any>((resolve, reject) => {
            return;
        });
    }

    downloadFile(FileId) {
        return new Promise<any>((resolve, reject) => {
            if (FileId) {
                firstValueFrom(this._StorageDataService
                    .getFileInfo(FileId))
                    .then((response) => {
                        const a = document.createElement('a');
                        a.href = response.body.Url;
                        a.download = response.body.Name;
                        document.body.appendChild(a);
                        a.click();
                        resolve({ Status: true });
                    });
            } else {
                resolve({ Status: false });
            }
        });
    }
    downloadDocumentFile(FileId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            if (FileId) {
                firstValueFrom(this._StorageDataService
                    .getFileInfo(FileId)
                    .pipe(take(1)))
                    .then((response) => {
                        const fileUrl = response.body.Url;
                        const fileName = response.body.Name;

                        if (fileUrl) {
                            firstValueFrom(this.http
                                .get(fileUrl, { responseType: 'blob' })
                                .pipe(take(1)))
                                .then((blob: Blob) => {
                                    const a = document.createElement('a');
                                    a.href = window.URL.createObjectURL(blob);
                                    a.download = fileName;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(a.href);

                                    resolve({ Status: true });
                                })
                                .catch((error) =>
                                    reject({
                                        Status: false,
                                        Error: new Error(error)
                                    })
                                );
                        } else {
                            resolve({ Status: false });
                        }
                    })
                    .catch((error) =>
                        reject({ Status: false, Error: new Error(error) })
                    );
            } else {
                resolve({ Status: false });
            }
        });
    }
    RemoveMultipleArtifact(selectedFiles: any[], fromModule) {
        const subject = new Subject();
        let counter = 0;
        const response: any[] = [];
        for (const file of selectedFiles) {
            this.RemoveObject(file, file.OrganizationId, fromModule).then(
                (resp) => {
                    response.push({
                        itemId: file.ItemId,
                        resp: resp
                    });
                    counter++;
                    if (counter == selectedFiles.length) {
                        subject.next(response);
                        subject.complete();
                    }
                }
            );
        }
        return subject.asObservable();
    }
    RemoveObject(item, OrganizationId, moduleSpecific, ZonetNoteImageItemId?) {
        let payload, endPoint, itemType, itemId;

        if (ZonetNoteImageItemId) {
            itemType = 'DriveFile';
            itemId = ZonetNoteImageItemId;
        } else {
            itemType = item.Type;
            itemId = item.ItemId;
        }
        return new Promise<any>((resolve, reject) => {
            switch (itemType) {
                case 'SharedFolder':
                    endPoint = 'DmsCommand/RemoveSharedFolder';
                    payload = {
                        SharedObjectArtifactId: itemId,
                        OrganizationId: OrganizationId
                    };
                    break;
                case 'DriveFolder':
                    endPoint = 'DmsCommand/DeleteObjectArtifact ';
                    payload = {
                        ObjectArtifactId: itemId
                    };
                    break;
                case 'SharedFile':
                    endPoint = 'DmsCommand/removesharedfile ';
                    payload = {
                        SharedObjectArtifactId: itemId,
                        OrganizationId: OrganizationId
                    };
                    break;
                case 'DriveFile':
                    endPoint = 'DmsCommand/DeleteObjectArtifact ';
                    payload = {
                        ObjectArtifactId: itemId
                    };
                    break;
            }
            if (moduleSpecific) {
                endPoint = 'DmsCommand/DeleteObjectArtifact ';
                payload = {
                    ObjectArtifactId: itemId
                };
            }
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        endPoint,
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }
    xRemoveMultipleObject(selectedFiles: any[]) {
        const aggreagetPayload = [];
        for (const file of selectedFiles) {
            let payload, endPoint;
            switch (file.Type) {
                case 'SharedFolder':
                    endPoint = 'DmsCommand/RemoveSharedFolder';
                    payload = {
                        SharedObjectArtifactId: file.ItemId,
                        OrganizationId: file.OrganizationId
                    };
                    break;
                case 'DriveFolder':
                    endPoint = 'DmsCommand/DeleteObjectArtifact';
                    payload = {
                        ObjectArtifactId: file.ItemId
                    };
                    break;
                case 'SharedFile':
                    endPoint = 'DmsCommand/RemoveSharedFile';
                    payload = {
                        SharedObjectArtifactId: file.ItemId,
                        OrganizationId: file.OrganizationId
                    };
                    break;
                case 'DriveFile':
                    endPoint = 'DmsCommand/DeleteObjectArtifact';
                    payload = {
                        ObjectArtifactId: file.ItemId
                    };
                    break;
            }
            aggreagetPayload.push({
                Uri:
                    ShellDomainProvider['api'].DocumentManagerService +
                    endPoint,
                Verb: 'Post',
                Payload: JSON.stringify(payload),
                SuccessIf: JSON.stringify({ Success: 'True' })
            });
        }
        const calls$ = aggreagetPayload.map(item =>
            this.http.post(item.Uri, JSON.parse(item.Payload), { headers: this.header, withCredentials: true, observe: 'response' })
                .pipe(catchError(() => of({ body: null })))
        );
        return forkJoin(calls$).pipe(
            map(() => ({ FailedCallIndex: -1, AggregateCallCompleted: true })),
            catchError(() => of({ FailedCallIndex: 0, AggregateCallCompleted: false }))
        );
    }

    ChangeFolderColor(ObjectId, Color) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                ItemId: ObjectId,
                Color: Color,
                Language: 'en-US',
                Tags: ['Is-Change-Folder-Color']
            };
            firstValueFrom(this.platformDataService
                .update('ObjectArtifact', payload))
                .then(
                    (resp) => {
                        if (resp['body'].StatusCode === 0) {
                            resolve(resp['body']);
                        } else {
                            reject(new Error(resp['body']));
                        }
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }
    AddOrRemoveFavorite(ObjectId, IsFavorite, SourceFileId, Type) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                IsFavorite: !IsFavorite,
                ObjectArtifactId: ObjectId
            };

            payload['SourceFileId'] = SourceFileId;

            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/AddToFavorite ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    RenameObject(ObjectId, NewName, SourceFileId, Type) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                NewName: NewName,
                ObjectArtifactId: ObjectId
            };
            payload['SourceFileId'] = SourceFileId;
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/RenameObjectArtifact ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    ShareFolder(FolderId, SharedPersonList, SharedRoleList, Tags?) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                ObjectArtifactId: FolderId,
                SharedPersonList: SharedPersonList,
                SharedRoleList: SharedRoleList,
                Tags: Tags || []
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/ShareFolder ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    GetPersonUserIds(PersonIds) {
        const subject = new Subject();
        this.getUserIdsByAggregate(PersonIds)
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    const userIds = [];
                    for (const person of PersonIds) {
                        userIds.push(resp[`${person}`].Results[0].ParentEntityID);
                    }
                    subject.next(userIds);
                    subject.complete();
                }
            });
        return subject.asObservable();
    }

    ShareFile(FileId, SharedPersonList, SharedRoleList, Tags?) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                ObjectArtifactId: FileId,
                SharedPersonList: SharedPersonList,
                SharedRoleList: SharedRoleList,
                Tags: Tags || []
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/ShareFile ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    Revoke(ItemId, Type, RevokedRoles, OldUserIds, NewPersonIds) {
        return new Promise<any>((resolve, reject) => {
            firstValueFrom(this.GetUserPersonIds(OldUserIds))
                .then(
                    (oldPersonIds: any) => {
                        const revokedPersonIds = oldPersonIds.filter(function (
                            obj
                        ) {
                            return NewPersonIds.indexOf(obj) === -1;
                        });
                        if (Type === 'Folder') {
                            this.RevokeFolderShare(
                                ItemId,
                                revokedPersonIds,
                                RevokedRoles
                            ).then(
                                (resp) => {
                                    resolve(resp);
                                },
                                (error) => {
                                    reject(new Error(error));
                                }
                            );
                        } else if (Type === 'File') {
                            this.RevokeFileShare(
                                ItemId,
                                revokedPersonIds,
                                RevokedRoles
                            ).then(
                                (resp) => {
                                    resolve(resp);
                                },
                                (error) => {
                                    reject(new Error(error));
                                }
                            );
                        }
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    RevokeFolderShare(FolderId, RevokedPersonList, RevokedRoleList) {
        return new Promise<any>((resolve, reject) => {
            firstValueFrom(this.GetPersonUserIds(RevokedPersonList))
                .then(
                    (UserIds) => {
                        const payload = {
                            SharedUserList: UserIds,
                            SharedRoleList: RevokedRoleList,
                            FolderId: FolderId
                        };
                        this.http
                            .post(
                                ShellDomainProvider['api']
                                    .DocumentManagerService +
                                    'DmsCommand/RevokeFolderShare ',
                                payload,
                                {
                                    headers: this.header,
                                    withCredentials: true,
                                    observe: 'body'
                                }
                            )
                            .pipe(take(1))
                            .subscribe({
                                next: (response: any) => {
                                    if (
                                        response.statusCode === 0 ||
                                        response.StatusCode === 0
                                    ) {
                                        resolve(response);
                                    } else {
                                        reject(new Error(response));
                                    }
                                },
                                error: (error) => {
                                    reject(new Error(error));
                                }
                            });
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    RevokeFileShare(FileId, RevokedPersonList, RevokedRoleList) {
        return new Promise<any>((resolve, reject) => {
            firstValueFrom(this.GetPersonUserIds(RevokedPersonList))
                .then(
                    (UserIds) => {
                        const payload = {
                            SharedUserList: UserIds,
                            SharedRoleList: RevokedRoleList,
                            FileId: FileId
                        };
                        this.http
                            .post(
                                ShellDomainProvider['api']
                                    .DocumentManagerService +
                                    'DmsCommand/RevokeFileShare ',
                                payload,
                                {
                                    headers: this.header,
                                    withCredentials: true,
                                    observe: 'body'
                                }
                            )
                            .pipe(take(1))
                            .subscribe({
                                next: (response: any) => {
                                    if (
                                        response.statusCode === 0 ||
                                        response.StatusCode === 0
                                    ) {
                                        resolve(response);
                                    } else {
                                        reject(new Error(response));
                                    }
                                },
                                error: (error) => {
                                    reject(new Error(error));
                                }
                            });
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    GetPersonsFromUserIds(UserIds) {
        return new Promise<any>((resolve, reject) => {
            firstValueFrom(this.GetUserPersonIds(UserIds))
                .then(
                    (PersonIds) => {
                        this.GetPersonsByIds(PersonIds).then(
                            (resp) => {
                                resolve(resp);
                            },
                            (error) => {
                                reject(new Error(error));
                            }
                        );
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    GetUserPersonIds(UserIds) {
        const subject = new Subject();
        this.getPersonIdsByAggregate(UserIds)
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    const personIds = [];
                    for (const user of UserIds) {
                        personIds.push(resp[`${user}`].Results[0].ChildEntityID);
                    }
                    subject.next(personIds);
                    subject.complete();
                }
            });
        return subject.asObservable();
    }

    GetObjectArtifactById(item, poweruser?) {
        let itemId: any = '';
        const fields = this.objectArtifactFields;
        const entity = 'ObjectArtifact';
        if (
            item &&
            (item.Type === 'SharedFolder' || item.Type === 'SharedFile')
        ) {
            if (item.Type === 'SharedFolder') {
                itemId = item.OriginalObjectArtifactId;
            }
            if (item.Type === 'SharedFile') {
                itemId = item.OriginalArtifactId;
            }
        }
        if (
            item &&
            (item.Type === 'DriveFolder' || item.Type === 'DriveFile')
        ) {
            itemId = item.ItemId;
        }
        if (typeof item === 'string') itemId = item;
        return new Promise<any>((resolve, reject) => {
            if (itemId) {
                const filters = [
                    {
                        property: 'ItemId',
                        operator: '=',
                        value: itemId
                    }
                ];
                const query = this.queryBuilderService.prepareQuery(
                    entity,
                    fields,
                    filters,
                    null,
                    0,
                    1
                );
                firstValueFrom(this.platformDataService
                    .getBySqlFilter(entity, query, null))
                    .then(
                        (Response) => {
                            if (Response.body.StatusCode === 0) {
                                resolve(Response.body.Results);
                            } else {
                                reject(new Error(Response.body));
                            }
                        },
                        (error) => {
                            reject(new Error(error));
                        }
                    );
            } else {
                resolve(null);
            }
        });
    }

    RevokeSharedFolder(FolderId, SharedUserList, SharedRoleList) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                FolderId: FolderId,
                SharedUserList: SharedUserList,
                SharedRoleList: SharedRoleList
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/RevokeFolderShare ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    RevokeSharedFile(FileId, SharedUserList, SharedRoleList) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                FileId: FileId,
                SharedUserList: SharedUserList,
                SharedRoleList: SharedRoleList
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/RevokeFolderShare ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    GetPersonsByIds(PersonIds) {
        return new Promise<any>((resolve, reject) => {
            const filter = [
                {
                    property: 'PersonId',
                    operator: 'contains',
                    value: PersonIds
                }
            ];
            const query = this.queryBuilderService.prepareQuery(
                'DeltaEmployee',
                ['ItemId', 'DisplayName', 'ProfileImage', 'Roles', 'PersonId'],
                filter,
                null,
                0,
                100
            );
            firstValueFrom(this.platformDataService
                .getBySqlFilter('DeltaEmployee', query, null))
                .then(
                    (resp) => {
                        if (resp.body.StatusCode === 0) {
                            if (resp.body.Results.length > 0) {
                                resolve(resp.body.Results);
                            } else {
                                reject(new Error('Not found'));
                            }
                        } else {
                            reject(new Error('Not found'));
                        }
                    },
                    (error) => {
                        reject(new Error(error));
                    }
                );
        });
    }

    RevokePersonIdsFromShareFolder(
        folderId,
        revokePersonIds,
        revokeRoles,
        tags?
    ) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                SharedPersonList: revokePersonIds,
                SharedRoleList: revokeRoles,
                FolderId: folderId,
                Tags: tags || []
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/RevokeFolderShare ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    RevokePersonIdsFromShareFile(fileId, revokePersonIds, revokeRoles, Tags?) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                SharedPersonList: revokePersonIds,
                SharedRoleList: revokeRoles,
                FileId: fileId,
                Tags: Tags || []
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/RevokeFileShare ',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }

    viewFile(item: DriveFile, secure = false) {
        // this.changeworkingState("busy");
        // this.GetObjectArtifactById(item).then((response) => {
        // this.changeworkingState("idle");
        // this.dialog.open(EcapFileViewerModalComponent, {
        //     data: {
        //         FileId: response[0].FileStorageId,
        //         Secure: secure,
        //     },
        // });
        // });
    }
    GetFolderOnly(ParentId, ModeuleWorkspaceId, key, archive) {
        return new Promise<any>((resolve, reject) => {
            const promises = [];
            promises.push(
                this.GetFolders(
                    ParentId,
                    ModeuleWorkspaceId,
                    key,
                    archive,
                    0,
                    100,
                    null
                )
            );
            promises.push(
                this.GetSharedFolders(ParentId, key, archive, 0, 100, null)
            );
            Promise.all(promises).then(
                (CombinedResp) => {
                    const folders: DriveFolder[] = [];
                    CombinedResp[0].forEach((folder) => {
                        folders.push(
                            new DriveFolder(
                                folder.ParentId,
                                folder.IsFavorite,
                                folder.OwnerName,
                                folder.SharedByUserName,
                                '',
                                '',
                                folder.OrganizationId,
                                folder.ItemId,
                                folder.Description,
                                folder.Name,
                                folder.Description,
                                folder.Color,
                                folder.CreateDate,
                                folder.SharedUserIdList,
                                folder.SharedRoleList,
                                ''
                            )
                        );
                    });
                    CombinedResp[1].forEach((folder) => {
                        const temp = find(folders, [
                            'ItemId',
                            folder['OriginalArtifactId']
                        ]);
                        if (temp) {
                            (temp.ParentId = folder.ParentId),
                                (temp.OrganizationId = folder.OrganizationId),
                                (temp.ItemId = folder.ItemId);
                            temp.Type = 'SharedFolder';
                            temp.SharedByUserName = folder.SharedByUserName;
                            temp.sharedToUserId = folder.SharedToUserId;
                            temp.OwnerId = folder.OwnerId;
                            temp.OriginalObjectArtifactId =
                                folder['OriginalArtifactId'];
                        } else {
                            folders.push(
                                new DriveFolder(
                                    folder.ParentId,
                                    folder.IsFavorite,
                                    folder.OwnerName,
                                    folder.SharedByUserName,
                                    folder.SharedToUserId,
                                    folder.OwnerId,
                                    folder.OrganizationId,
                                    folder.ItemId,
                                    folder.Description.folder.Name,
                                    folder.Description,
                                    folder.Color,
                                    folder.CreateDate,
                                    folder.SharedUserIdList,
                                    folder.SharedRoleList,
                                    folder['OriginalArtifactId'],
                                    folder.Type
                                )
                            );
                        }
                    });
                    resolve(folders);
                },
                (error) => {
                    reject(new Error(error));
                }
            );
        });
    }
    public getRoleList(): Observable<any> {
        const subject = new Subject();
        this.deltagqlService
            .getEntityData(
                'AppRole',
                ['ItemId', 'Title', 'Key'],
                `IsFeatureSpecificRole:false, IsActive:true, IsHidden:false`,
                0,
                100,
                `SortingOrder:1`
            )
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    subject.next(resp.Data);
                    subject.complete();
                },
                error: (err) => {
                    subject.next([]);
                    subject.complete();
                }
            });
        return subject.asObservable();
    }
    public getEmployees(key: string): Observable<any> {
        const subject = new Subject();
        this.deltagqlService
            .getEntityData(
                'DeltaEmployee',
                DeltaEmployeeSchema,
                `Active:true,$or:[{DisplayName:/${key}/i},{Email:/${key}/i}]`,
                0,
                20,
                `DisplayName:1`
            )
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    const personIds: string[] = resp.Data.map(
                        (item) => item.PersonId
                    );
                    this.getUserIdsByAggregate(personIds)
                        .pipe(take(1))
                        .subscribe({
                            next: (res) => {
                                resp.Data = resp.Data.map((item) => {
                                    const userId =
                                        res[`${item.PersonId}`].Results[0]
                                            .ParentEntityID;
                                    return {
                                        ...item,
                                        UserId: userId
                                    };
                                });
                                subject.next({
                                    response: resp.Data,
                                    key: key
                                });
                                subject.complete();
                            }
                        });
                },
                error: (err) => {
                    subject.next([]);
                    subject.complete();
                }
            });
        return subject.asObservable();
    }
    getUserIdsByAggregate(personIds) {
        const validIds = personIds.filter(id => !!id);
        if (!validIds.length) return of({ FailedCallIndex: -1, AggregateCallCompleted: true });
        const connectionsUrl = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetConnections';
        const calls$ = validIds.map(personId => {
            const connectQuery = this.queryBuilderService.prepareGetConnectionQuery('User', '', 'Person', personId, true, ['Person-For-User']);
            connectQuery['PageNumber'] = 0;
            connectQuery['PageLimit'] = 100;
            connectQuery['ExpandParent'] = false;
            connectQuery['ExpandChild'] = false;
            return this.http.post(connectionsUrl, connectQuery, { headers: this.header, withCredentials: true, observe: 'response' })
                .pipe(catchError(() => of({ body: null })));
        });
        return forkJoin(calls$).pipe(map((responses: any[]) => {
            const result: any = { FailedCallIndex: -1, AggregateCallCompleted: true };
            validIds.forEach((id, i) => { result[id] = responses[i].body; });
            return result;
        }));
    }
    getPersonIdsByAggregate(userIds) {
        const validIds = userIds.filter(id => !!id);
        if (!validIds.length) return of({ FailedCallIndex: -1, AggregateCallCompleted: true });
        const connectionsUrl = ShellDomainProvider['api'].DataCore + 'DataManipulationQuery/GetConnections';
        const calls$ = validIds.map(userId => {
            const connectQuery = this.queryBuilderService.prepareGetConnectionQuery('User', userId, 'Person', '', true, ['Person-For-User']);
            connectQuery['PageNumber'] = 0;
            connectQuery['PageLimit'] = 100;
            connectQuery['ExpandParent'] = false;
            connectQuery['ExpandChild'] = false;
            return this.http.post(connectionsUrl, connectQuery, { headers: this.header, withCredentials: true, observe: 'response' })
                .pipe(catchError(() => of({ body: null })));
        });
        return forkJoin(calls$).pipe(map((responses: any[]) => {
            const result: any = { FailedCallIndex: -1, AggregateCallCompleted: true };
            validIds.forEach((id, i) => { result[id] = responses[i].body; });
            return result;
        }));
    }
    GetEmployeesFromUserIds(userIds) {
        const subject = new Subject();
        const personUserMap = {};
        this.getPersonIdsByAggregate(userIds)
            .pipe(take(1))
            .subscribe({
                next: (resp) => {
                    const personIds = [];
                    for (const userId of userIds) {
                        personIds.push(resp[`${userId}`].Results[0].ChildEntityID);
                        personUserMap[resp[`${userId}`].Results[0].ChildEntityID] =
                            userId;
                    }
                    const ids = "'" + personIds.map((d) => d).join("','") + "'";
                    const filter = `PersonId: {$in: [${ids}]}`;
                    this.deltagqlService
                        .getEntityData(
                            'DeltaEmployee',
                            DeltaEmployeeSchema,
                            filter,
                            0,
                            100,
                            `DisplayName:1`
                        )
                        .pipe(take(1))
                        .subscribe({
                            next: (res) => {
                                const ret = res.Data.map((item) => {
                                    return {
                                        ...item,
                                        UserId: personUserMap[item.PersonId]
                                    };
                                });
                                subject.next(ret);
                                subject.complete();
                            }
                        });
                }
            });
        return subject.asObservable();
    }
    isFolderExists(parentId, workspaceId, organizationId): Observable<any> {
        const subject = new Subject();
        const fields = this.objectArtifactFields;
        const filters = [
            {
                property: 'ArtifactType',
                operator: '=',
                value: '1'
            },
            {
                property: 'ParentId',
                operator: '=',
                value: parentId
            },
            {
                property: 'WorkSpaceId',
                operator: '=',
                value: workspaceId
            },
            {
                property: 'OrganizationId',
                operator: '=',
                value: organizationId
            }
        ];
        const query = this.queryBuilderService.prepareQuery(
            'ObjectArtifact',
            fields,
            filters,
            null,
            0,
            100
        );
        this.platformDataService
            .getBySqlFilter('ObjectArtifact', query, null)
            .pipe(take(1))
            .subscribe({
                next: (Response) => {
                    if (Response.body.StatusCode === 0) {
                        subject.next(Response.body.Results);
                        subject.complete();
                    } else {
                        subject.next('error');
                        subject.complete();
                    }
                },
                error: (error) => {
                    subject.next('error');
                    subject.complete();
                }
            });
        return subject.asObservable();
    }
    isChildFolderExists(ParentId, Name, WorkspaceId): Observable<any> {
        const subject = new Subject();
        const fields = this.objectArtifactFields;
        const filters = [
            {
                property: 'Name',
                operator: '=',
                value: Name
            },
            {
                property: 'ParentId',
                operator: '=',
                value: ParentId
            },
            {
                property: 'WorkSpaceId',
                operator: '=',
                value: WorkspaceId
            }
        ];
        const query = this.queryBuilderService.prepareQuery(
            'ObjectArtifact',
            fields,
            filters,
            'CreateDate DESC',
            0,
            100
        );
        this.platformDataService
            .getBySqlFilter('ObjectArtifact', query, null)
            .pipe(take(1))
            .subscribe({
                next: (Response) => {
                    if (Response.body.StatusCode === 0) {
                        subject.next(Response.body.Results);
                        subject.complete();
                    } else {
                        subject.next('error');
                        subject.complete();
                    }
                },
                error: (error) => {
                    subject.next('error');
                    subject.complete();
                }
            });
        return subject.asObservable();
    }
    AddOrRemoveFromArchive(artifactId, changeStatusTo) {
        return new Promise<any>((resolve, reject) => {
            const payload = {
                ObjectArtifactId: artifactId,
                IsArchived: changeStatusTo
            };
            this.http
                .post(
                    ShellDomainProvider['api'].DocumentManagerService +
                        'DmsCommand/ArchiveObjectArtifact',
                    payload,
                    {
                        headers: this.header,
                        withCredentials: true,
                        observe: 'body'
                    }
                )
                .pipe(take(1))
                .subscribe({
                    next: (response: any) => {
                        if (
                            response.statusCode === 0 ||
                            response.StatusCode === 0
                        ) {
                            resolve(response);
                        } else {
                            reject(new Error(response));
                        }
                    },
                    error: (error) => {
                        reject(new Error(error));
                    }
                });
        });
    }
}
export const DeltaEmployeeSchema = [
    'ItemId',
    'DisplayName',
    'Email',
    'PersonId',
    'PersonnelNumber',
    'ProfileImageId'
];
